import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Geist's spinner: a ring of bars that never moves — only their opacity
 * chases clockwise, which is why it stays crisp at 12px where a rotating arc
 * smears.
 *
 * Per size: box, bar count, bar length × thickness (px), cycle (ms).
 */
const SIZES = {
  sm: { box: 12, bars: 8, length: 3, thickness: 1.5, duration: 1000 },
  md: { box: 16, bars: 10, length: 4, thickness: 1.5, duration: 1000 },
  lg: { box: 20, bars: 12, length: 5, thickness: 2, duration: 1200 },
  xl: { box: 24, bars: 12, length: 6, thickness: 2.5, duration: 1200 },
  "2xl": { box: 32, bars: 15, length: 8, thickness: 2.5, duration: 1200 },
  "3xl": { box: 40, bars: 15, length: 10, thickness: 3, duration: 1200 },
  "4xl": { box: 56, bars: 18, length: 14, thickness: 3.5, duration: 1300 },
} as const;

type SpinnerSize = keyof typeof SIZES;

function Spinner({
  size = "md",
  className,
  style,
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & { size?: SpinnerSize }) {
  const s = SIZES[size] ?? SIZES.md;
  const step = s.duration / s.bars;
  return (
    <div
      role="status"
      aria-label="Loading"
      data-slot="spinner"
      className={cn(
        "relative inline-block aspect-square shrink-0 transform-gpu text-gray-700",
        className,
      )}
      style={
        {
          width: s.box,
          height: s.box,
          "--spinner-duration": `${s.duration}ms`,
          ...style,
        } as React.CSSProperties
      }
      {...props}
    >
      {Array.from({ length: s.bars }, (_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: the bars are a fixed, positional ring
          key={i}
          aria-hidden
          className="absolute top-1/2 left-1/2 animate-spinner rounded-full bg-current will-change-transform"
          style={{
            width: s.length,
            height: s.thickness,
            // The 0° bar starts furthest through its fade, so the bright one
            // travels clockwise.
            animationDelay: `${-(s.bars - 1 - i) * step}ms`,
            transform: `translate(-50%, -50%) rotate(${(i * 360) / s.bars}deg) translate(146%)`,
          }}
        />
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
}

export { Spinner, type SpinnerSize };
